'use client'

import { useEffect, useRef } from 'react'

import { usePathname, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'

import { trackUserSignup } from '@/lib/utils/analytics'

if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
    api_host: '/analytics-relay',
    capture_pageview: false, // Disable automatic pageview capture, as we capture manually
    ui_host: 'https://us.posthog.com',
  })
}

export function PostHogPageview() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (pathname) {
      let url = window.origin + pathname
      if (searchParams && searchParams.toString()) {
        url = url + `?${searchParams.toString()}`
      }
      posthog.capture('$pageview', {
        $current_url: url,
      })
    }
  }, [pathname, searchParams])

  return <></>
}

export const PostHogProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: session } = useSession()
  const hasTrackedSignup = useRef(false)

  useEffect(() => {
    if (session) {
      // First identify the user
      posthog?.identify(session?.user?.id, {
        email: session?.user?.email,
        userId: session?.user?.id,
      })

      posthog.setPersonPropertiesForFlags({
        email: session?.user?.email,
      })

      // Then track signup after identification
      if (session.user.isNewUser && session.user.id && !hasTrackedSignup.current) {
        trackUserSignup({ method: session.user.authMethod })
        hasTrackedSignup.current = true
      }
    }
  }, [session])
  return <PHProvider client={posthog}>{children}</PHProvider>
}
